import { FeatureFlagIds } from "../../../utils/featureFlagIds";
import React, {useEffect} from 'react';
import Button from '@mui/material/Button';
import {useDispatch, useSelector, useStore} from 'react-redux';
import {AppState} from '../../../store/rootReducer';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import {IRuleViolation} from '../../../Interfaces/Application/IApplicationQuote';
import {useDecisions} from '../../../hooks/Decisions';
import {useDialog} from '../../../providers/DialogProvider';
import ThumbUp from '@mui/icons-material/ThumbUp';
import ThumbDown from '@mui/icons-material/ThumbDown';
import ThumbsUpDown from '@mui/icons-material/ThumbsUpDown';
import {useEmployeeRolePermissions} from '../../../Data/EmployeeRoleData';
import {useAvailableStatusWorkflowActions} from '../../../hooks/useAvailableStatusWorkflowActions';
import { usePostedAppLock } from '../../../hooks/usePostedAppLock';
import DocumentsAndNotesDialog, {DocumentsAndNotesDialogResults} from '../../Dialogs/DocumentsAndNotesDialog';
import {businessRuleOverrideStatusTypes, Constants, INCOMPLETE_INSURANCE_MESSAGE, permissions} from "../../../utils/constants";
import {useConfiguration} from "../../../Data/ConfigurationData";
import {IApplicationNotes} from '../../../Interfaces/Application/IApplicationNotes';
import SendIcon from "@mui/icons-material/Send";
import QuestionMark from '@mui/icons-material/QuestionMark';
import {IApplicationStatusWorkflowItem} from "../../../Interfaces/Application/IApplicationStatusWorkflowItem";
import {getSelectedFinanceQuote} from "../../../utils/quoteUtils";
import FirstPaymentAndExtraPaymentDialog
  from "../../Dialogs/FirstPaymentAndExtraPaymentDialog/FirstPaymentAndExtraPaymentDialog";
import BypassableErrorDialog from "../../Dialogs/BypassableErrorDialog";
import {ConfirmHardPullRequirement} from "../../Dialogs/ConfirmHardPullRequirement";
import {ApplicationQuoteType, hasValue, PermissionSet, toDollars, Vehicle} from "@amsanalytics/react-shared-library";
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {IApplication} from '../../../Interfaces/Application/IApplication';
import {Tooltip} from '@mui/material';
import {useLoanPostingDialog} from "../../../hooks/UseLoanPosting";
import VehicleEditorDialog from "../../Dialogs/VehicleEditorDialog/VehicleEditorDialog";
import {userHasPermission} from "../../../utils/utils";
import {updateAmsVehicleAsync} from "../../../Data/VehicleData";
import {
  AppStatusesToRequireAdverseActionReasons
} from '../../../Interfaces/Enums/AppStatusesToRequireAdverseActionReasons';
import AdverseActionsDialog from '../../Dialogs/AdverseActionsDialog/AdverseActionsDialog';
import {CreateIndirectDecisionDenialReason} from '../../../Data/CreateIndirectDecisionDenialReason';
import {createDecisionDenialReasons, loadIndirectStipsAndDecisionReasons} from '../../../store/dealerService/actions';
import {moveForwardApp} from '../../../store/application/actions';
import ErrorDialog from "../../Dialogs/ErrorDialog";
import {requestLeaseQuoteSolve} from "../../../Data/LeaseQuoteData";
import {solveLeaseQuoteSuccess} from "../../../store/application/types";
import {getLeaseAutoSolve, getLeaseVehicleEditEffect} from "../../../utils/leaseQuoteSolveUtils";
import {getFirstPaymentReview} from "../../../utils/firstPaymentReview";
import {useLeaseQuoteRefusal} from "../../../hooks/UseLeaseQuoteRefusal";
import {Window, WindowActionsBar} from '@progress/kendo-react-dialogs';
import {Button as KendoButton} from '@progress/kendo-react-buttons';
import {httpGetAsync} from '../../../utils/HttpClient';
import {authService} from '../../../index';

export interface StatusWorkflowComponentProps {
  disableDeskingOrPosting?: boolean;
  style?: React.CSSProperties;
}

/**
 * Calls the lightweight CustomerVehicleInsurance/byApplication endpoint to check whether
 * any insurance records have incomplete required fields.
 * Required fields: Insurance Company, Policy Number, Stock Number, Effective Date, Expiration Date.
 * Returns true if insurance is incomplete or missing (should block), false if all records are complete.
 */
export const validateInsuranceForPostingAsync = async (applicationId: string): Promise<boolean> => {
  try {
    const bearerToken = await authService.getToken();
    const response = await httpGetAsync(
      `api/CustomerVehicleInsurance/byApplication/${applicationId}`,
      bearerToken!
    );
    if (!response || !response.ok) return false;
    const insurances: any[] = await response.json();

    // Block if no insurance records exist at all
    if (insurances.length === 0) {
      return false;
    }

    // Block if any record has incomplete required fields
    return insurances.some((ins: any) =>
      !ins.insuranceCompany || !ins.policyNumber || !ins.stockNumber || !ins.effectiveDate || !ins.expirationDate
    );
  } catch {
    return false;
  }
};

export const StatusWorkflowComponent: React.FC<StatusWorkflowComponentProps> = (props) => {
  const { data: configuration } = useConfiguration();
  const currentApp = useSelector((state: AppState) => state.applications.editingApp);
  const store = useStore<AppState>();
  const financeQuoteErrors = useSelector((state: AppState) => state.notifications.financeQuoteErrors);
  const leaseQuoteErrors = useSelector((state: AppState) => state.notifications.leaseQuoteErrors);
  const applicationStatuses = useSelector((state: AppState) => state.applications.appStatuses);
  const dealerServiceAppHistoryItems = useSelector((state: AppState) => state.applications.dealerServiceAppHistoryItems);
  const disabledApplicationStatuses = useSelector((state: AppState) => state.applications.disabledAppStatuses);
  const [canView, canEdit, canAct] = useEmployeeRolePermissions();
  const { getAvailableActions } = useAvailableStatusWorkflowActions();
  const { activeUnlock, isSectionLocked, lockMessage } = usePostedAppLock();
  const isStatusLocked = isSectionLocked('Status');
  // Rule violations on the app's own quote gate status changes.
  const [submitApp, updateApplicationStatus] = useDecisions(
    currentApp?.quoteType === ApplicationQuoteType.Lease ? leaseQuoteErrors : financeQuoteErrors);
  const [openDialog, closeDialog] = useDialog();
  const keyFactorValidationResults = useSelector((state: AppState) => state.applications.keyFactorValidationResults);
  const businessRuleOverrides = useSelector((state: AppState) => state.applications.editingApp?.businessRuleOverrides ?? []);
  const availableAppStatuses = currentApp?.appStatusId && currentApp?.quoteType != null
    ? getAvailableActions(currentApp.appStatusId, currentApp.quoteType)
    : [];
  const lookups = useSelector((state: AppState) => state.applications.lookups);
  const activeUser = useSelector((state: AppState) => state.applications.loggedInUser?.employee);
  const adverseActionReasons = useSelector((state: AppState) => state.dealerService.adverseActionReasons);
  const dealerServiceDecisionReasonCodes = useSelector((state: AppState) => state.applications.lookups.dealerServiceDecisionReasonCodes);
  const dispatch = useDispatch();

  useEffect(() => {
    if (currentApp) {
      dispatch(loadIndirectStipsAndDecisionReasons(currentApp!.id))
    }
  }, [currentApp, dealerServiceAppHistoryItems]);

  const nextStatusIsDisabled = (nextApplicationStatusId: string): boolean => {
    // If they are desking or posting directly to AMS and it's supposed to be prevented, return early
    if (props.disableDeskingOrPosting && (Constants.applicationStatuses.desking.toUpperCase() === nextApplicationStatusId.toUpperCase()
      || (configuration?.featureFlags[FeatureFlagIds.ClientPostsLoansDirectlyToAms] && Constants.applicationStatuses.finalizedPosted.toUpperCase() === nextApplicationStatusId.toUpperCase()))) {
      return true;
    }

    if (disabledApplicationStatuses.map(s => s.toUpperCase()).includes(nextApplicationStatusId.toUpperCase())) {
      return true;
    }

    return false;
  }

  const updateApplicationStatusFromStatusWorkflowComponent = (appToSave: IApplication, applicationStatusWorkflowItem: IApplicationStatusWorkflowItem, userBypassedSoftPullRequirement: boolean) => {
    updateApplicationStatus(
      appToSave,
      applicationStatusWorkflowItem.nextStatusId,
      configuration?.featureFlags[FeatureFlagIds.UseInternalAmsScore],
      userBypassedSoftPullRequirement,
      applicationStatusWorkflowItem.businessRuleAction,
      (errors: (string | IRuleViolation)[], bypassErrorCallback: () => void) => {
        openDialog({
          children: (
            <BypassableErrorDialog
              bodyText={"This application does not meet all requirements."}
              errors={errors}
              allowBypass={(errors as IRuleViolation[]).filter(e => e.type === 'error')?.length === 0}
              title={'Rule Violations'}
              accept={() => {
                closeDialog();
                bypassErrorCallback();
              }}
              closeDialog={closeDialog}
            />
          ),
        });
      },
      applicationStatusWorkflowItem.indirectDecisionType,
      applicationStatusWorkflowItem.requestNotes,
      applicationStatusWorkflowItem.requestDocuments,
      applicationStatusWorkflowItem.requestNotesPrompt,
      (handleStatusChange: (applicationNotes?: IApplicationNotes[]) => void, requestNotes?: boolean, requestDocuments?: boolean, requestNotesPrompt?: string, userBypassedSoftPullRequirement?: boolean) => {
        if (requestNotes || requestDocuments) {
          openDialog({
            children: (
              <DocumentsAndNotesDialog
                applicationId={currentApp?.id!}
                requestDocuments={requestDocuments}
                requestNotes={requestNotes}
                requestNotesPrompt={requestNotesPrompt}
                accept={(results: DocumentsAndNotesDialogResults) => {
                  // Add new notes to application.
                  if (results.notes !== undefined) {
                    // currentApp!.applicationNotes = [...currentApp!.applicationNotes, ...results.notes];
                    handleStatusChange(results.notes);
                  } else {
                    handleStatusChange();
                  }
                  closeDialog();
                }}
                closeDialog={closeDialog}
              />
            ),
          });
        }
      }
    );
  }

  const [openLoanPostingDialog, conditionallyOpenUnfinishedCardDownPaymentDialog] = useLoanPostingDialog(updateApplicationStatusFromStatusWorkflowComponent);
  const showRefusalFor = useLeaseQuoteRefusal();

  /* BEGIN This section handles the logic for determining if decisions should be disabled based on failed key factors and waived business rules */
  const failedKeyFactors = keyFactorValidationResults.filter((kfvr) => kfvr.section === 1);
  const waivedBusinessRules = businessRuleOverrides.filter(override => override.businessRuleOverrideStatusId?.toLowerCase() === businessRuleOverrideStatusTypes.waived?.toLowerCase());
  
  // Check if the current application status is in the list of statuses that should not have decisions disabled
  const currentStatusIsInOverrideList = lookups.validationRuleStatusOverrides?.some(
    statusId => statusId.toUpperCase() === currentApp?.appStatusId?.toUpperCase()
  ) ?? false;
  
  const disableDecisions = !currentStatusIsInOverrideList && configuration?.featureFlags?.[FeatureFlagIds.OverrideBusinessRules] === true && failedKeyFactors.some(failedFactor => {
    // Find if this factor has a matching waived override
    const hasWaivedOverride = waivedBusinessRules.some(override =>
      (override.applicationField?.toLowerCase() === failedFactor.threshold.fieldName?.toLowerCase()
      || override.validationFieldName?.toLowerCase() === failedFactor.threshold.fieldName?.toLowerCase() )
      &&
      override.businessRuleOverrideStatusId?.toLowerCase() === businessRuleOverrideStatusTypes.waived?.toLowerCase()
    );

    // If there's no waived override for this factor, it contributes to disabling decisions
    return !hasWaivedOverride;
  });
  /* END of section for disabling decisions based on failed key factors and waived business rules */

  /** Returns a tooltip string explaining why a given workflow button is disabled, or '' if it's enabled. */
  const getDisabledReasonTooltip = (as: IApplicationStatusWorkflowItem): string => {
    if (isStatusLocked) return lockMessage;
    if (disableDecisions && as.label !== 'Deny') {
      const unwaived = failedKeyFactors.filter(f => {
        return !waivedBusinessRules.some(o =>
          (o.applicationField?.toLowerCase() === f.threshold.fieldName?.toLowerCase()
            || o.validationFieldName?.toLowerCase() === f.threshold.fieldName?.toLowerCase())
          && o.businessRuleOverrideStatusId?.toLowerCase() === businessRuleOverrideStatusTypes.waived?.toLowerCase());
      });
      if (unwaived.length > 0) {
        const names = unwaived.map(f => f.threshold.fieldName).join(', ');
        return `Disabled due to validation rule failures: ${names}. Waive the failed rules or resolve the issues to enable this action.`;
      }
      return 'Disabled due to validation rule failures.';
    }
    if (nextStatusIsDisabled(as.nextStatusId)) {
      if (props.disableDeskingOrPosting
        && (Constants.applicationStatuses.desking.toUpperCase() === as.nextStatusId.toUpperCase()
          || (configuration?.featureFlags[FeatureFlagIds.ClientPostsLoansDirectlyToAms] && Constants.applicationStatuses.finalizedPosted.toUpperCase() === as.nextStatusId.toUpperCase()))) {
        return 'Desking/posting is disabled because required checklists or stipulations are incomplete.';
      }
      if (disabledApplicationStatuses.map(s => s.toUpperCase()).includes(as.nextStatusId.toUpperCase())) {
        return 'This status is currently disabled by business rules.';
      }
    }
    return '';
  };

  const saveAppFromStatusWorkflowComponentAsync = async (applicationStatusWorkflowItem: IApplicationStatusWorkflowItem, userBypassedSoftPullRequirement: boolean) => {
    const newStatusIsPostedAndFinalizedStatusAndTheyPostDirectly = applicationStatusWorkflowItem.nextStatusId.toLowerCase() === Constants.applicationStatuses.finalizedPosted.toLowerCase()
      && configuration?.featureFlags?.[FeatureFlagIds.ClientPostsLoansDirectlyToAms];

    // If the next status is Finalized and Posted and client posts directly to AMS, run through loan posting checks
    if (newStatusIsPostedAndFinalizedStatusAndTheyPostDirectly) {
      await openLoanPostingDialog(applicationStatusWorkflowItem, userBypassedSoftPullRequirement);
    } else {
      // Desking moves the deposits to AMS 2000's application number, so a card down payment AMS doesn't have yet stops it
      // (LOS card down payments A12); the server refuses it too.
      const newStatusIsDesking = applicationStatusWorkflowItem.nextStatusId.toUpperCase() === Constants.applicationStatuses.desking.toUpperCase();
      if (newStatusIsDesking && !(await conditionallyOpenUnfinishedCardDownPaymentDialog())) {
        return;
      }
      // The latest page state: the checks before this point can re-solve the lease quote.
      updateApplicationStatusFromStatusWorkflowComponent(store.getState().applications.editingApp ?? currentApp!, applicationStatusWorkflowItem, userBypassedSoftPullRequirement);
    }
  }

  const selectedFinanceQuote = getSelectedFinanceQuote(currentApp);

  // A saved vehicle edit can change a Lease app's selling price or ACV; re-solve before desking or posting.
  const resolveLeaseQuoteAfterVehicleEditAsync = async (vehicle: Vehicle): Promise<boolean> => {
    const effect = getLeaseVehicleEditEffect(currentApp, vehicle);
    if (!effect.needsSolve) {
      return true;
    }
    if (effect.negotiatedPriceReplaced) {
      const message = `Saving the vehicle set the lease selling price to the vehicle's price (${toDollars(vehicle.carPrice)}). Check the selling price and solve the lease quote, then try again.`;
      openDialog({ children: <ErrorDialog title="Selling Price Changed" errors={[message]} closeDialog={closeDialog} /> });
      return false;
    }
    // The vehicle update already put the saved price into the page's quote.
    const applications = store.getState().applications;
    const autoSolve = getLeaseAutoSolve(applications.editingApp ?? currentApp!, applications.leaseQuoteSolvedRollback);
    if (!autoSolve) {
      return true; // Not solved before the edit either: the save refuses to desk or post until it is.
    }
    try {
      const result = await requestLeaseQuoteSolve(autoSolve.application, autoSolve.mode);
      dispatch(solveLeaseQuoteSuccess(result));
      if (result.errors.length === 0) {
        return true;
      }
      openDialog({ children: <ErrorDialog title="Quote / Solve Errors" errors={result.errors} closeDialog={closeDialog} /> });
    } catch (error: any) {
      openDialog({ children: <ErrorDialog title="Quote / Solve Failed" errors={[error?.message ?? String(error)]} closeDialog={closeDialog} /> });
    }
    return false;
  };

  const conditionallyOpenVehicleErrorsDialogAsync = async (): Promise<boolean> => {
    if (!currentApp?.vehicle) {
      // No vehicle attached — skip vehicle validation checks
      return true;
    }

    return new Promise(resolve => {
      const vehicleOrganization = lookups.organizations.find(o => o.id === currentApp!.vehicle!.organizationId);
      const vehicleLocationOfPurchase = lookups.vehicleLocationsOfPurchase.find(o => o.id === currentApp!.vehicle!.locationOfPurchaseId);

      if (!hasValue(vehicleOrganization?.amsCode) || !vehicleLocationOfPurchase) {
        openDialog({
          children: (
            <VehicleEditorDialog
              vehicle={currentApp!.vehicle!}
              canEditInventory={activeUser && userHasPermission(activeUser, permissions.inventoryMileage, PermissionSet.U)}
              canEditFullVehicle={configuration?.featureFlags?.[FeatureFlagIds.AllowVehicleFullEdit] === true}
              onClose={closeDialog}
              onSaveVehicle={async (vehicle: Vehicle) => {
                const saved = await updateAmsVehicleAsync(dispatch, vehicle, currentApp!.id, (currentApp!.quoteType === ApplicationQuoteType.Indirect));
                closeDialog();
                resolve(saved ? await resolveLeaseQuoteAfterVehicleEditAsync(vehicle) : true);
              }}
            />
          ),
        });
      } else if (currentApp!.vehicle!.carStatus !== 'AV') {
        openDialog({
          children: (
            <ErrorDialog
              title="Vehicle Not Available"
              errors={['You cannot post the loan with a vehicle that is not marked as available.']}
              closeDialog={closeDialog} // exits and returns false
            />
          ),
        });
      } else {
        // On to the next check or dialog
        resolve(true);
      }
    });
  }

  const handleStatusChangeAsync = async (applicationStatusWorkflowItem: IApplicationStatusWorkflowItem) => {
    // If the next status is Dealer Review, show confirmation dialog and call moveForwardApp
    const nextStatusIsDealerReview = applicationStatusWorkflowItem.nextStatusId.toUpperCase() === Constants.applicationStatuses.dealerReview.toUpperCase();
    if (nextStatusIsDealerReview) {
      openDialog({
        children: (
          <Window title="Confirm Move Forward" onClose={closeDialog} width={450} height={220}>
            <p style={{ margin: '10px 0' }}>
              This will convert the indirect quote to a finance quote and move the application to Dealer Review. This action cannot be undone.
            </p>
            <WindowActionsBar>
              <KendoButton onClick={closeDialog}>Cancel</KendoButton>
              <KendoButton themeColor="primary" onClick={() => {
                closeDialog();
                dispatch(moveForwardApp() as any);
              }}>Move Forward</KendoButton>
            </WindowActionsBar>
          </Window>
        ),
      });
      return;
    }

    let userBypassedSoftPullRequirement: boolean | undefined = undefined;
    const nextStatusIsDesking = applicationStatusWorkflowItem.nextStatusId.toUpperCase() === Constants.applicationStatuses.desking.toUpperCase();
    const nextStatusIsFinalizedPosted = applicationStatusWorkflowItem.nextStatusId.toUpperCase() === Constants.applicationStatuses.finalizedPosted.toUpperCase();
    const nextStatusIsPostedAndFinalizedAndTheLoanIsBeingPosted = nextStatusIsFinalizedPosted
      && configuration?.featureFlags[FeatureFlagIds.ClientPostsLoansDirectlyToAms];

    // Whether we should prompt the user to select adverse action reasons before changing the app status.
    // Show this if the list of adverseActionReasons is empty, AND if one of the following are true:
    // 1. The next status is Denied and the lookups.appStatusesToRequireAdverseActionReasons value is 1 or 3
    // 2. The next status is Conditionally Approved and the lookups.appStatusesToRequireAdverseActionReasons value is 2 or 3
    const shouldPromptForAdverseActionReasons = adverseActionReasons.length === 0 && (currentApp?.quoteType !== ApplicationQuoteType.Indirect) &&
      ((applicationStatusWorkflowItem.nextStatusId.toUpperCase() === Constants.applicationStatuses.denied.toUpperCase() && (lookups.appStatusesToRequireAdverseActionReasons === AppStatusesToRequireAdverseActionReasons.Denied || lookups.appStatusesToRequireAdverseActionReasons === AppStatusesToRequireAdverseActionReasons.Both)
      ) || (applicationStatusWorkflowItem.nextStatusId.toUpperCase() === Constants.applicationStatuses.conditionallyApproved.toUpperCase() && (lookups.appStatusesToRequireAdverseActionReasons === AppStatusesToRequireAdverseActionReasons.ConditionallyApproved || lookups.appStatusesToRequireAdverseActionReasons === AppStatusesToRequireAdverseActionReasons.Both)));

    if (nextStatusIsDesking || nextStatusIsPostedAndFinalizedAndTheLoanIsBeingPosted) {
      // Checked again after the vehicle step below, which can change the quote.
      const leaseAction = nextStatusIsDesking ? 'desking' : 'posting';
      if (currentApp!.quoteType === ApplicationQuoteType.Lease && showRefusalFor(leaseAction)) {
        return;
      }

      // Validate required insurance fields when desking or posting loan
      if (nextStatusIsFinalizedPosted || nextStatusIsDesking) {
        const hasIncompleteInsurance = await validateInsuranceForPostingAsync(currentApp!.id);
        if (hasIncompleteInsurance) {
          openDialog({
            children: (
              <Window title="Incomplete Vehicle Insurance" onClose={closeDialog} width={500} height={250}>
                <p style={{ margin: '10px 0' }}>{INCOMPLETE_INSURANCE_MESSAGE}</p>
                <WindowActionsBar>
                  <KendoButton themeColor="primary" onClick={closeDialog}>OK</KendoButton>
                </WindowActionsBar>
              </Window>
            ),
          });
          return;
        }
      }

      // Check to see if any of the applicants have only had a soft pull
      userBypassedSoftPullRequirement = await ConfirmHardPullRequirement(currentApp!, openDialog, closeDialog);
      if (userBypassedSoftPullRequirement === false) {
        return;
      }

      // If the application is being posted or the quote isn't an indirect quote, we need to check if the vehicle is ready to go
      // The vehicle is used by AMS 2000 when we desk a finance or lease app OR the loan is posted
      if (nextStatusIsPostedAndFinalizedAndTheLoanIsBeingPosted || (currentApp!.quoteType !== ApplicationQuoteType.Indirect)) {
        const continueAfterVehicleCheck = await conditionallyOpenVehicleErrorsDialogAsync();
        if (!continueAfterVehicleCheck) {
          return;
        }
      }

      // The vehicle check above can re-solve a lease, so a Lease app is checked and reviewed from the latest state.
      if (currentApp!.quoteType === ApplicationQuoteType.Lease && showRefusalFor(leaseAction)) {
        return;
      }
      const firstPaymentReview = getFirstPaymentReview(store.getState().applications.editingApp ?? currentApp!, selectedFinanceQuote);
      if (firstPaymentReview) {
        const postOrDesk = nextStatusIsDesking
          ? "DESK"
          : "POST";

        openDialog({
          children: (
            <FirstPaymentAndExtraPaymentDialog
              review={firstPaymentReview}
              accept={async () => {
                closeDialog();
                await saveAppFromStatusWorkflowComponentAsync(applicationStatusWorkflowItem, userBypassedSoftPullRequirement ?? false);
              }}
              closeDialog={() => {
                // Returns early if they don't like the first payment date or deferred down payment dates
                closeDialog();
              }}
            />
          )
        });
        } else {
            await saveAppFromStatusWorkflowComponentAsync(applicationStatusWorkflowItem, userBypassedSoftPullRequirement ?? false);
        }
    } else if (shouldPromptForAdverseActionReasons) {
      openDialog({
        children: (
          <AdverseActionsDialog
            title='Choose Adverse Action Reasons'
            dealerServiceDecisionReasonCodes={dealerServiceDecisionReasonCodes}
            closeDialog={closeDialog}
            accept={(selectedDenialReasonIds: string[]) => {
              // Create the array of CreateIndirectDecisionDenialReason objects. Sequence number will begin with indirectDecisionReasons.length, and increment by 1 for each selectedDenialReasons. Order by selectedDenialReason.sortOrder
              const startSequenceNumber = adverseActionReasons ? adverseActionReasons.length : 0;
              // Sort the selected denial reasons by sortOrder so we can assign sequence numbers in proper order
              const sortedDenialReasons = dealerServiceDecisionReasonCodes.filter(reason => selectedDenialReasonIds.includes(reason.id)).sort((reason1, reason2) => (reason1.sortOrder || 0) - (reason2.sortOrder || 0));
              const createDenialReasons: CreateIndirectDecisionDenialReason[] = sortedDenialReasons.map((reason, index) => ({
                dealerServiceDecisionReasonCodeId: reason.id,
                applicationId: currentApp!.id,
                sequenceNumber: startSequenceNumber + index
              }));
              // Dispatch the action to create the denial reasons
              dispatch(createDecisionDenialReasons({ createDenialReasons }));
              closeDialog();
              saveAppFromStatusWorkflowComponentAsync(applicationStatusWorkflowItem, userBypassedSoftPullRequirement ?? false);
            }}
          />
        ),
      });
    } else {
      // Validate required insurance fields when desking or posting loan (even when not posting directly to AMS)
      if (nextStatusIsFinalizedPosted || nextStatusIsDesking) {
        const hasIncompleteInsurance = await validateInsuranceForPostingAsync(currentApp!.id);
        if (hasIncompleteInsurance) {
          openDialog({
            children: (
              <Window title="Incomplete Vehicle Insurance" onClose={closeDialog} width={500} height={250}>
                <p style={{ margin: '10px 0' }}>{INCOMPLETE_INSURANCE_MESSAGE}</p>
                <WindowActionsBar>
                  <KendoButton themeColor="primary" onClick={closeDialog}>OK</KendoButton>
                </WindowActionsBar>
              </Window>
            ),
          });
          return;
        }
      }
      await saveAppFromStatusWorkflowComponentAsync(applicationStatusWorkflowItem, userBypassedSoftPullRequirement ?? false);
    }
  };

  return (
    <div style={{ display: 'flex', padding: '5px', alignItems: 'center', gap: '10px', ...props.style, border: 'none' }}>
      {disableDecisions && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
          <Tooltip title='Some decisions are disabled due to validation rule errors.' arrow>
            <ErrorOutlineIcon fontSize='small' color='error' />
          </Tooltip>
        </div>
      )}
      {availableAppStatuses.sort((a, b) => (applicationStatuses.find(s => s.id.toUpperCase() === a.nextStatusId?.toUpperCase())?.sortOrder ?? 0) - (applicationStatuses.find(s => s.id.toUpperCase() === b.nextStatusId?.toUpperCase())?.sortOrder ?? 0))
        .map((as, key) => (
        <Tooltip key={key} title={getDisabledReasonTooltip(as)} placement="top">
          <span>
            <Button
              style={{ height: '35px', textWrap: 'nowrap', textTransform: 'none', minWidth: '100px' }}
              startIcon={
            as.icon === undefined ? (
              <></>
            ) : as.icon === 'NavigateNext' ? (
              <NavigateNextIcon fontSize='small' />
            ) : as.icon === 'ThumbsUp' ? (
              <ThumbUp fontSize='small' />
            ) : as.icon === 'ThumbsUpDown' ? (
              <ThumbsUpDown fontSize='small' />
            ) : as.icon === 'ThumbsDown' ? (
              <ThumbDown fontSize='small' />
            ) : as.icon === 'Send' ? (
              <SendIcon fontSize='small' />
            ) : as.icon === 'QuestionMark' ? (
              <QuestionMark fontSize='small' />
            ) : (
              <></>
            )
          }
          onClick={async () => await handleStatusChangeAsync(as)}
          variant='contained'
          color={as.color ?? 'inherit'}
          disabled={(disableDecisions && as.label !== 'Deny') || isStatusLocked || nextStatusIsDisabled(as.nextStatusId)}
        >
          {as.label}
        </Button>
          </span>
        </Tooltip>

      ))
      }
      {/* </Grid> */}
    </div >
  );
};
